import type { NombreIcono } from '../../componentes/iconos';
import type { TarjetaAyuda } from '../../marco/cabecera';
import type { SesionAbierta } from '../../api/ops/cuenta';

/** Apartados de «Mi cuenta» (1.8) con los textos del diseño (`1.8-mi-cuenta-y-seguridad.html`). */
export type Apartado = 'perfil' | 'entrar' | 'seguridad' | 'sesiones' | 'pin';

export const APARTADOS: readonly { clave: Apartado; etiqueta: string; icono: NombreIcono }[] = [
    { clave: 'perfil', etiqueta: 'Perfil', icono: 'user' },
    { clave: 'entrar', etiqueta: 'Entrar', icono: 'log-in' },
    { clave: 'seguridad', etiqueta: 'Seguridad', icono: 'shield-check' },
    { clave: 'sesiones', etiqueta: 'Sesiones', icono: 'laptop' },
    { clave: 'pin', etiqueta: 'PIN', icono: 'key-round' },
];

export const AYUDA: Record<Apartado, TarjetaAyuda[]> = {
    perfil: [
        { icono: 'user', titulo: 'Tus datos', texto: 'Nombre, email e idioma de tu cuenta. Los ves igual en todos los negocios donde trabajas.' },
        { icono: 'mail', titulo: 'Cambiar el email', texto: 'Te mandamos un enlace al email nuevo y avisamos al actual. Hasta que lo abras, sigues entrando con el actual.' },
    ],
    entrar: [
        { icono: 'log-in', titulo: 'Formas de entrar', texto: 'Contraseña, Google o Apple. Puedes tener varias; nunca te quedas sin ninguna.' },
        { icono: 'shield-check', titulo: 'Y los dos pasos', texto: 'Se piden al entrar con contraseña. Con Google o Apple no, salvo para transferir la propiedad, dar de baja el negocio o dejar que Soporte haga cambios.' },
    ],
    seguridad: [
        { icono: 'shield-check', titulo: 'Dos pasos', texto: 'Además de la contraseña te pedimos un código de tu móvil. Es opcional para el equipo del negocio.' },
        { icono: 'list-checks', titulo: 'Códigos de recuperación', texto: 'Sirven si pierdes el móvil. Cada uno vale una vez; guárdalos fuera de Reservaria.' },
    ],
    sesiones: [
        { icono: 'laptop', titulo: 'Sesiones abiertas', texto: 'Los dispositivos donde has entrado. Cierra los que no reconozcas.' },
        { icono: 'log-out', titulo: 'Cerrar todas las demás', texto: 'Si pierdes un móvil, ciérralas todas de una vez. Esta sesión sigue abierta.' },
    ],
    pin: [
        { icono: 'key-round', titulo: 'PIN de tablet', texto: 'De 4 a 6 cifras para entrar en las tablets del negocio. Solo lo sabes tú.' },
        { icono: 'lock', titulo: 'Si te equivocas', texto: 'Tras 5 intentos fallidos, el PIN se bloquea 15 minutos o hasta que elijas uno nuevo aquí.' },
    ],
};

export const IDIOMAS: readonly [string, string][] = [
    ['es-ES', 'Español'],
    ['ca-ES', 'Català'],
    ['en-GB', 'English'],
    ['pt-PT', 'Português'],
];

export const METODO: Record<string, string> = { password: 'Con contraseña', google: 'Con Google', apple: 'Con Apple' };
export const PROVEEDOR: Record<string, string> = { google: 'Google', apple: 'Apple' };

export const PIE = {
    perfil: 'Tus clientes no ven estos datos. Son los de tu cuenta de acceso al panel.',
    entrar: 'Con Google o Apple no te pedimos el código de los dos pasos al entrar. Sí te lo pedimos para transferir la propiedad, dar de baja el negocio o dejar que Soporte haga cambios.',
    pin: 'Sin PIN no puedes entrar en las tablets del negocio. Cada negocio tiene su PIN. Tras 5 intentos fallidos se bloquea 15 minutos; si eliges uno nuevo aquí, se desbloquea al momento.',
    soloEsta: 'Solo tienes abierta esta sesión.',
} as const;

export const ERRORES = {
    nombre: 'Escribe tu nombre y apellidos.',
    email: 'Escribe un email válido.',
    mismoEmail: 'Es el email que ya usas.',
    actual: 'Escribe la contraseña actual.',
    actualMal: 'La contraseña no es correcta.',
    minimo: 'Mínimo 12 caracteres.',
    noCoinciden: 'No coinciden.',
    codigo: 'Escribe los 6 números.',
    pin: 'De 4 a 6 cifras.',
    guardar: 'No he podido guardarlo. Prueba de nuevo.',
} as const;

/** «14 de julio de 2026». */
export function fechaLarga(iso: string): string {
    return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Madrid' });
}

/** «21:05», en la zona del negocio cuando se sabe (`memberships[].timezone` de getMe, G375); si no, la de Madrid. */
export function hora(iso: string, zona?: string | null): string {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: zona ?? 'Europe/Madrid' });
}

/** «Safari · Mac» a partir del navegador de la sesión (o su nombre, si lo tiene). */
export function dispositivo(s: Pick<SesionAbierta, 'device_name' | 'user_agent'>): string {
    if (s.device_name != null && s.device_name !== '') {
        return s.device_name;
    }
    const ua = s.user_agent ?? '';
    const navegador = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
    const sistema = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android' : /Mac OS X|Macintosh/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
    return sistema === '' ? navegador : `${navegador} · ${sistema}`;
}

export function esMovil(s: Pick<SesionAbierta, 'user_agent'>): boolean {
    return /iPhone|Android|iPad/.test(s.user_agent ?? '');
}

/** «Caseta Embrujo, Sala Andén y Peluquería Lola». */
export function unir(nombres: readonly string[]): string {
    if (nombres.length <= 1) {
        return nombres[0] ?? '';
    }
    return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1] ?? ''}`;
}
